import AlreadySignedUpDialog from "@components/AlreadySignedUpDialog"
import Button from "@components/Button"
import Input from "@components/Input"
import Layout from "@components/Layout"
import PageHeader from "@components/PageHeader"
import { trackCompleteRegistrationEvent } from "@helpers/facebookTracking"
import {
  getPasswordFromSessionStorage,
  getUserId,
  removePasswordFromSessionStorage,
} from "@helpers/sessionStorage"
import { signUp, storeGender } from "@helpers/supabase"
import { validateEmail } from "@helpers/validations"
import * as Sentry from "@sentry/react"
import { setUserId } from "@snowplow/browser-tracker"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

export default function EmailPage() {
  const [email, setEmail] = useState("")
  const [emailValid, setEmailValid] = useState(true)
  const [password, setPassword] = useState("")
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmailValid = validateEmail(email)

  const doSignUp = async () => {
    setIsSubmitting(true)

    const { error } = await signUp({ email, password })

    if (error) {
      if (error.message === "User already registered") {
        setOpenAlreadySignedUpDialog(true)
      } else {
        Sentry.captureException(error)
      }

      setIsSubmitting(false)
      setShowConfirm(false)
      return
    }

    await storeGender()

    removePasswordFromSessionStorage()

    const userId = await getUserId()

    if (userId) {
      setUserId(userId)
    }

    await trackCompleteRegistrationEvent()

    setIsSubmitting(false)

    navigate("/sign-up/preparing")
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (isSubmitting) return
    event.preventDefault()

    if (!isEmailValid) {
      setEmailValid(false)
      return
    }

    setShowConfirm(true)
  }

  useEffect(() => {
    const passwordFromSessionStorage = getPasswordFromSessionStorage()

    if (passwordFromSessionStorage) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPassword(passwordFromSessionStorage)
    } else {
      navigate("/sign-up/password")
    }
  }, [navigate])

  const hasError = !emailValid

  const getSubheading = () => {
    if (!emailValid) return t("pages.signUp.email.emailNotValid")
    return t("pages.signUp.email.subheading")
  }

  if (showConfirm) {
    return (
      <Layout>
        <PageHeader
          emoji="✉️"
          heading={t("components.emailPreSignupConfirm.title")}
          subheading={t("components.emailPreSignupConfirm.subtitle")}
        />

        <div className="flex grow flex-col justify-between">
          <div className="space-y-4">
            <p className="text-center text-xl font-semibold break-all">
              {email}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              disabled={isSubmitting}
              onClick={doSignUp}
              title={t("components.emailPreSignupConfirm.confirm")}
            />
            <button
              className="w-full py-2 text-sm text-gray-400 transition-colors hover:text-gray-300"
              disabled={isSubmitting}
              onClick={() => setShowConfirm(false)}
              type="button"
            >
              {t("components.emailPreSignupConfirm.edit")}
            </button>
          </div>
        </div>

        <AlreadySignedUpDialog
          open={openAlreadySignedUpDialog}
          onClose={() => setOpenAlreadySignedUpDialog(false)}
        />
      </Layout>
    )
  }

  return (
    <>
      <Layout>
        <PageHeader
          emoji="✉️"
          heading={t("pages.signUp.email.heading")}
          subheading={getSubheading()}
          hasError={hasError}
        />

        <form className="custom-form" noValidate onSubmit={handleSubmit}>
          <div className="flex grow flex-col justify-between">
            <div className="space-y-4">
              <Input
                autoFocus
                value={email}
                description={
                  <Trans
                    components={{
                      privacyPolicyLink: (
                        <Link
                          className="underline"
                          target="_blank"
                          to={"https://www.filterly.app/privacy"}
                        />
                      ),
                      termsAndConditionsLink: (
                        <Link
                          className="underline"
                          target="_blank"
                          to={"https://www.filterly.app/terms"}
                        />
                      ),
                    }}
                    i18nKey="pages.signUp.email.text"
                  />
                }
                invalid={!emailValid}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setEmailValid(true)
                }}
                placeholder={t("shared.email")}
                type="email"
              />
            </div>

            <Button
              disabled={email.length < 1 || isSubmitting}
              type="submit"
              title={t("shared.continue")}
            />
          </div>
        </form>
      </Layout>

      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
